import { TAG_PRODUCT_NEAR_EXPIRED } from 'sysconfig';

/**
 * Policy hiển thị tag sản phẩm.
 *
 * Nguồn sự thật là registry tag của CMS (`zustand.tags`, nạp qua SsrService -> MockService.getTags).
 * Module này cố tình không phụ thuộc React / zustand để có thể unit-test và để phía export
 * (XLSX) dùng lại mà không kéo theo MUI.
 *
 * Rule:
 *  R1 - chỉ hiển thị code có trong registry và `visible === true`.
 *  R2 - thứ tự: `priority` giảm dần -> vị trí trong registry tăng dần -> code tăng dần.
 *  R3 - dedupe theo code sau khi alias.
 *  R6 - `NEAR_EXPIRATION` (FE tự sinh) là alias của code cận date bên CMS; `PROMOTIONS` -> `DEAL`.
 *  R7 - nhãn lấy từ `name` của CMS, i18n chỉ là fallback.
 */

// Code do FE tự sinh ở ProductServiceV2/WebService từ lotDates[].isNearExpired.
export const LEGACY_NEAR_EXPIRED_CODE = 'NEAR_EXPIRATION';

// BE không có tag DEAL, FE không có PROMOTIONS -> quy về một code.
const STATIC_ALIASES = { PROMOTIONS: 'DEAL' };

// Tập key `tag.*` đang tồn tại trong public/locales/*/common.json.
// Chỉ dùng làm fallback cho code KHÔNG có entry CMS visible. Không thêm key mới ở đây:
// đa ngôn ngữ cho tag là bài toán dữ liệu CMS (per-locale `name`).
const TAG_I18N_FALLBACK = new Set([
  'DEAL',
  'LIMIT',
  'NEAR_EXPIRATION',
  'HOADONNHANH',
  '3ABC',
  '2ABC',
  'PROMOTION',
  'FV2U',
]);

const EMPTY_REGISTRY = new Map();

/**
 * Chuẩn hoá danh sách code lấy từ `product.tags`: bỏ giá trị rỗng, áp alias, dedupe.
 *
 * `nearExpiredCode` rỗng (ví dụ .env.local không set NEXT_PUBLIC_TAG_PRODUCT_NEAR_EXPIRE)
 * thì giữ nguyên NEAR_EXPIRATION để còn fallback qua i18n, thay vì alias sang chuỗi rỗng.
 */
export const normalizeTagCodes = (
  codes = [],
  { nearExpiredCode = TAG_PRODUCT_NEAR_EXPIRED } = {},
) => {
  if (!Array.isArray(codes)) return [];

  const seen = new Set();
  const result = [];

  codes.forEach((rawCode) => {
    if (!rawCode) return;

    let code = STATIC_ALIASES[rawCode] || rawCode;
    if (code === LEGACY_NEAR_EXPIRED_CODE && nearExpiredCode) code = nearExpiredCode;

    if (seen.has(code)) return;
    seen.add(code);
    result.push(code);
  });

  return result;
};

/** Dựng Map code -> tag (chỉ tag visible), kèm vị trí gốc để tie-break ổn định. */
export const buildTagRegistry = (tags = []) => {
  if (!Array.isArray(tags) || tags.length === 0) return EMPTY_REGISTRY;

  const registry = new Map();
  tags.forEach((tag, index) => {
    if (!tag?.code || !tag.visible || registry.has(tag.code)) return;
    registry.set(tag.code, { ...tag, registryIndex: index });
  });
  return registry;
};

// Registry chỉ đổi khi mảng `tags` trong store đổi identity (mỗi lần hydrate SSR),
// nên memo theo identity là đủ và tránh quét tuyến tính mỗi lần render card.
const REGISTRY_CACHE = new WeakMap();

export const getTagRegistry = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) return EMPTY_REGISTRY;

  const cached = REGISTRY_CACHE.get(tags);
  if (cached) return cached;

  const registry = buildTagRegistry(tags);
  REGISTRY_CACHE.set(tags, registry);
  return registry;
};

const toPriority = (value) => (Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY);

// R2. Ba khoá, vì thứ tự đầu vào (`product.tags`) khác nhau giữa các đường dữ liệu BE,
// nên chỉ dựa vào tính ổn định của Array.sort là chưa đủ.
const compareTags = (a, b) => {
  const priorityA = toPriority(a.priority);
  const priorityB = toPriority(b.priority);
  if (priorityA !== priorityB) return priorityB - priorityA;

  if (a.registryIndex !== b.registryIndex) return a.registryIndex - b.registryIndex;

  return String(a.code).localeCompare(String(b.code));
};

/**
 * Lọc + sắp xếp + cắt theo giới hạn của surface.
 * Trả về object tag đã resolve để tầng render không phải hỏi lại store.
 */
export const resolveVisibleTags = ({ codes = [], registry = EMPTY_REGISTRY, max = Infinity }) => {
  if (!registry?.size || !codes?.length) return [];

  const resolved = [];
  codes.forEach((code) => {
    const tag = registry.get(code);
    if (tag) resolved.push(tag);
  });

  resolved.sort(compareTags);

  return Number.isFinite(max) ? resolved.slice(0, Math.max(0, max)) : resolved;
};

/** R7. `name` của CMS thắng; i18n chỉ đỡ cho code không có entry CMS visible. */
export const getTagLabel = ({ tag = null, code = null, t = null } = {}) => {
  if (tag?.name) return tag.name;

  const key = code || tag?.code;
  if (key && TAG_I18N_FALLBACK.has(key) && typeof t === 'function') return t(`tag.${key}`);

  return '';
};

export default {
  LEGACY_NEAR_EXPIRED_CODE,
  normalizeTagCodes,
  buildTagRegistry,
  getTagRegistry,
  resolveVisibleTags,
  getTagLabel,
};
